/**
 * Global config driven by a single active profile (API / signaling / video / ICE).
 * Profile table mirrored from accent-fleet-12, with **absolute** URLs for Capacitor
 * (no reverse proxy — every path is prefixed with `https://{serverHost}`).
 *
 * Default profile: {@link ACTIVE_PROFILE} in `active-profile.ts`.
 */

import { Injectable } from '@angular/core';
import { ACTIVE_PROFILE } from './active-profile';
import type { ProfileName } from './profile-names';

export type { ProfileName } from './profile-names';

/** Firebase Web Push config (browser). Native Android/iOS use google-services / GoogleService-Info. */
export interface FirebaseWebConfig {
  apiKey: string;
  authDomain: string;
  projectId: string;
  storageBucket: string;
  messagingSenderId: string;
  appId: string;
  measurementId?: string;
  /** Web Push certificates key pair from Firebase Console → Cloud Messaging. */
  vapidKey: string;
}

interface ProfileConfig {
  /** Absolute API base, e.g. `https://traci.tn/ws_rimtrack_all/`. */
  dns: string;
  /** Host used by GlobalService when the user switches server at runtime. */
  serverHost: string;
  /** Absolute assets root, e.g. `https://traci.tn/`. */
  assetsBase: string;
  /** Absolute files root (no trailing slash), e.g. `https://traci.tn`. */
  filesDir: string;
  nominatim_dns: string;
  adIframeUrl: string;
  currency: string;
  client: string;
  rapportsDir: string;
  addok_dns: string;
  wsocket: string;
  videoUrl: string;
  turnServerUrl?: string;
  turnUsername?: string;
  turnPassword?: string;
  stunServerUrl?: string;
  /** Branding / UI skin key used by sign-in & assets (`traci`, `fleet`, `finek`…). */
  brand: string;
  fcmservername: string;
  policy: string;
  /** Browser FCM web app config for this profile (optional until web app is created). */
  firebase?: FirebaseWebConfig;
}

/** TRACI Firebase web app (project traci-216708). Also used by accentfrance / localhost. */
const FIREBASE_TRACI: FirebaseWebConfig = {
  apiKey: 'AIzaSyCJebGtqM-Tch-Nooe_wOYjwGmLCrkLiAs',
  authDomain: 'traci-216708.firebaseapp.com',
  projectId: 'traci-216708',
  storageBucket: 'traci-216708.firebasestorage.app',
  messagingSenderId: '524615104342',
  appId: '1:524615104342:web:906b9ec789656f480f48f7',
  measurementId: 'G-V5V8D1NWXW',
  vapidKey: 'BDfjLv8nunTPGPYW5TE2H_8K4PtLhvXIdSXxUz3eGQKW-rYQR7PAZNu-4FOXkMGPBejbNVcruycElwXyPbhiwhc',
};

/** FINEK Firebase web app (project finek-6335a). */
const FIREBASE_FINEK: FirebaseWebConfig = {
  apiKey: 'AIzaSyCYXYAH8G8UueFBX1Wb_5yG2h_CrnFjHFc',
  authDomain: 'finek-6335a.firebaseapp.com',
  projectId: 'finek-6335a',
  storageBucket: 'finek-6335a.firebasestorage.app',
  messagingSenderId: '187479161017',
  appId: '1:187479161017:web:d4aae4d34574977cda4368',
  measurementId: 'G-HS92QHZ7B7',
  vapidKey: 'BOt2ew_eQsrxWL_COhx5F62TCx8mAhK_kWODcFm3JErXs0qMCAAiLzlUDOpa8kHz0q4xK2B9SKJoyheBdla7WnU',
};

/**
 * FLEET Firebase web app (project fleet-dd3a4).
 * Shared by fleet / gps216 / cpg1 / slama / stir / dev / dev7 / dev9.
 */
const FIREBASE_FLEET: FirebaseWebConfig = {
  apiKey: 'AIzaSyBPKlybcQ33HmCkXQUrd6wWGtv4n-jTXz8',
  authDomain: 'fleet-dd3a4.firebaseapp.com',
  projectId: 'fleet-dd3a4',
  storageBucket: 'fleet-dd3a4.firebasestorage.app',
  messagingSenderId: '741771501163',
  appId: '1:741771501163:web:36e5c16abea1fb30e89b2e',
  measurementId: 'G-3QN0702W70',
  vapidKey: 'BCxCMlO5mQZrvRy1PeZp3nk_NClIr5MEkjotBhPSq_FN7clP-XKxulIYcpcV63abhSFIifCEwsnO_37BDRe-d0E',
};

const DEFAULT_TURN_SERVER_URL = 'turn:turn.ttn.tn:3478';
const DEFAULT_TURN_USERNAME = 'masmoudi';
const DEFAULT_TURN_PASSWORD = 'masmoudi';
const DEFAULT_STUN_SERVER_URL = 'stun:stun.ttn.tn:3478';

/** Prefer https except for local Tomcat / localhost. */
function protoForHost(serverHost: string): string {
  return serverHost.startsWith('127.') || serverHost.startsWith('localhost') ? 'http://' : 'https://';
}

/**
 * Build an absolute URL from a fleet-style relative path and a server host.
 * Absolute `http(s):` / `ws(s):` / `turn:` / `stun:` values are left unchanged.
 */
function absoluteUrl(pathOrUrl: string, serverHost: string, options?: { trailingSlash?: boolean }): string {
  const raw = (pathOrUrl ?? '').trim();
  const proto = protoForHost(serverHost);
  const host = serverHost.replace(/\/+$/, '');

  if (!raw || raw === '/') {
    const base = `${proto}${host}`;
    return options?.trailingSlash === false ? base : `${base}/`;
  }

  if (/^(https?:|wss?:|turn:|stun:)/i.test(raw)) {
    if (options?.trailingSlash === true && !raw.endsWith('/')) {
      return `${raw}/`;
    }
    if (options?.trailingSlash === false) {
      return raw.replace(/\/+$/, '');
    }
    return raw;
  }

  const path = raw.startsWith('/') ? raw : `/${raw}`;
  let url = `${proto}${host}${path}`;
  url = url.replace(/([^:]\/)\/+/g, '$1');

  if (options?.trailingSlash === true) {
    return url.replace(/\/+$/, '') + '/';
  }
  if (options?.trailingSlash === false) {
    return url.replace(/\/+$/, '');
  }
  return url;
}

/** Relative fleet paths → absolute mobile URLs for one profile. */
function profile(
  serverHost: string,
  partial: {
    dnsPath: string;
    brand: string;
    fcmservername: string;
    currency: string;
    client: string;
    wsocket: string;
    videoUrl: string;
    policyPath: string;
    filesDirPath?: string;
    assetsBasePath?: string;
    nominatimPath?: string;
    adIframePath?: string;
    rapportsPath?: string;
    addokPath?: string;
    turnServerUrl?: string;
    turnUsername?: string;
    turnPassword?: string;
    stunServerUrl?: string;
    firebase?: FirebaseWebConfig;
  }
): ProfileConfig {
  const filesPath = partial.filesDirPath ?? '/';
  return {
    serverHost,
    dns: absoluteUrl(partial.dnsPath, serverHost, { trailingSlash: true }),
    assetsBase: absoluteUrl(partial.assetsBasePath ?? '/', serverHost, { trailingSlash: true }),
    filesDir: absoluteUrl(filesPath === '' ? '/' : filesPath, serverHost, { trailingSlash: false }),
    nominatim_dns: absoluteUrl(partial.nominatimPath ?? '/nominatim', serverHost),
    adIframeUrl: absoluteUrl(partial.adIframePath ?? '/if3/adIfFound.html', serverHost),
    currency: partial.currency,
    client: partial.client,
    rapportsDir: absoluteUrl(partial.rapportsPath ?? '/rapports/', serverHost, { trailingSlash: true }),
    addok_dns: absoluteUrl(partial.addokPath ?? '/addok/', serverHost, { trailingSlash: true }),
    wsocket: partial.wsocket,
    videoUrl: absoluteUrl(partial.videoUrl, serverHost, { trailingSlash: true }),
    turnServerUrl: partial.turnServerUrl ?? DEFAULT_TURN_SERVER_URL,
    turnUsername: partial.turnUsername ?? DEFAULT_TURN_USERNAME,
    turnPassword: partial.turnPassword ?? DEFAULT_TURN_PASSWORD,
    stunServerUrl: partial.stunServerUrl ?? DEFAULT_STUN_SERVER_URL,
    brand: partial.brand,
    fcmservername: partial.fcmservername,
    policy: absoluteUrl(partial.policyPath, serverHost),
    firebase: partial.firebase,
  };
}

const ICE = {
  turnServerUrl: DEFAULT_TURN_SERVER_URL,
  turnUsername: DEFAULT_TURN_USERNAME,
  turnPassword: DEFAULT_TURN_PASSWORD,
  stunServerUrl: DEFAULT_STUN_SERVER_URL,
};

const PROFILES: Record<ProfileName, ProfileConfig> = {
  slama: profile('gpstds.lumiere.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'slama',
    fcmservername: 'slama',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/SLAMA.html',
    firebase: FIREBASE_FLEET,
    ...ICE,
  }),
  traci: profile('traci.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'traci',
    fcmservername: 'traci',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.traci.tn/ws/',
    videoUrl: 'https://web.dashcam.traci.tn/uploads/',
    policyPath: '/if3/policy/TRACI.html',
    firebase: FIREBASE_TRACI,
    ...ICE,
  }),
  fleet: profile('fleet.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    firebase: FIREBASE_FLEET,
    ...ICE,
  }),
  gps216: profile('fleet.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    firebase: FIREBASE_FLEET,
    ...ICE,
  }),
  cpg1: profile('fleet.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    firebase: FIREBASE_FLEET,
    ...ICE,
  }),
  dev7: profile('fleet.tn', {
    dnsPath: '/ws_rimtrack_all_dev7/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    firebase: FIREBASE_FLEET,
    ...ICE,
  }),
  dev9: profile('fleet.tn', {
    dnsPath: '/ws_rimtrack_all_dev9/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    firebase: FIREBASE_FLEET,
    ...ICE,
  }),
  /** Same video/ICE stack as fleet-12; API path matches fleet production. */
  dev: profile('fleet.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    firebase: FIREBASE_FLEET,
    ...ICE,
  }),
  finek: profile('finek.com.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'finek',
    fcmservername: 'finek',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fastrack.tn/ws/',
    videoUrl: 'https://web.dashcam.fastrack.tn/uploads/',
    policyPath: '/if3/policy/FINEK.html',
    filesDirPath: '',
    firebase: FIREBASE_FINEK,
    ...ICE,
  }),
  stir: profile('fleet.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    firebase: FIREBASE_FLEET,
    ...ICE,
  }),
  accentfrance: profile('fleet.tn', {
    dnsPath: '/ws_rimtrack_all/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'EUR',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: 'https://web.dashcam.fleet.tn/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    firebase: FIREBASE_TRACI,
    ...ICE,
  }),
  localhost: profile('127.0.0.1:8080', {
    dnsPath: 'http://127.0.0.1:8080/',
    brand: 'fleet',
    fcmservername: 'fleet',
    currency: 'TND',
    client: 'OTHERS',
    wsocket: 'wss://signalling.fleet.tn/ws/',
    videoUrl: '/uploads/',
    policyPath: '/if3/policy/FLEET.html',
    nominatimPath: '/nominatim/',
    firebase: FIREBASE_TRACI,
    ...ICE,
  }),
};

const C = PROFILES[ACTIVE_PROFILE];

/** Path suffix after origin, used when GlobalService switches serverHost at runtime. */
function pathAfterOrigin(absoluteUrlValue: string, fallbackPath: string): string {
  if (/^https?:\/\//i.test(absoluteUrlValue)) {
    try {
      const u = new URL(absoluteUrlValue);
      return u.pathname + u.search + u.hash || fallbackPath;
    } catch {
      return fallbackPath;
    }
  }
  return fallbackPath;
}

export const ACTIVE_PROFILE_NAME: ProfileName = ACTIVE_PROFILE;

export var version: string = C.brand;
export var template: string = C.brand;
export var mobile: string = 'false';

export var dns: string = C.dns;
export var imagesDir: string = absoluteUrl('/images/', C.serverHost, { trailingSlash: true });
export var imagesCarsDir: string = absoluteUrl('/images/cars/', C.serverHost, { trailingSlash: true });
export var imagesPOIDir: string = absoluteUrl('/images/poi/', C.serverHost, { trailingSlash: true });
export var imagesMODELDir: string = absoluteUrl('/images/models/', C.serverHost, { trailingSlash: true });
export var imagesAssetDir: string = absoluteUrl('/images/assets/', C.serverHost, { trailingSlash: true });
export var filesDir: string = C.filesDir;
export var nominatim_dns: string = C.nominatim_dns;
export var adIframeUrl: string = C.adIframeUrl;
export var rapportsDir: string = C.rapportsDir;
export var addok_dns: string = C.addok_dns;
export var currency: string = C.currency;
export var client: string = C.client;
export var token_google = 'AIzaSyCKznGfI2X7tF1K1NsLHVTcKrf9-HmMwBI';

export var wsocket: string = C.wsocket;
export var videoUrl: string = C.videoUrl;

export var turnServerUrl: string = C.turnServerUrl || DEFAULT_TURN_SERVER_URL;
export var turnUsername: string = C.turnUsername || DEFAULT_TURN_USERNAME;
export var turnPassword: string = C.turnPassword || DEFAULT_TURN_PASSWORD;
export var stunServerUrl: string = C.stunServerUrl || DEFAULT_STUN_SERVER_URL;

/** Active profile Firebase web config (null if this profile has no web FCM app yet). */
export const firebaseWebConfig: FirebaseWebConfig | null = C.firebase ?? null;

export var time_difference = new Date().getTimezoneOffset();

@Injectable()
export class GlobalService {
  dns: string;
  imagesCarsDir: string;
  nominatim_dns: string;
  imagesPOIDir: string;
  imagesMODELDir: string;
  imagesDir: string;
  filesDir: string;
  imagesAssetDir: string;
  time_difference: number;
  servername: string = C.serverHost;
  proto: string = protoForHost(C.serverHost);
  policy: string;
  addok_dns: string;
  fcmservername: string = C.fcmservername;
  firebase: FirebaseWebConfig | null = C.firebase ?? null;

  private readonly apiPath = pathAfterOrigin(C.dns, '/ws_rimtrack_all/');
  private readonly nominatimPath = pathAfterOrigin(C.nominatim_dns, '/nominatim');
  private readonly addokPath = pathAfterOrigin(C.addok_dns, '/addok/');
  private readonly policyPath = pathAfterOrigin(C.policy, '/if3/policy/TRACI.html');
  private readonly filesPath = pathAfterOrigin(C.filesDir + '/', '/');

  constructor() {
    this.updateUrl();
  }

  getFirebaseConfig(): FirebaseWebConfig | null {
    return this.firebase;
  }

  getservername() {
    return this.servername;
  }

  /**
   * Custom servers whose hostname starts with "dev" (e.g. dev.x.tn, dev9.fleet.tn)
   * use /ws_rimtrack_all_dev/ when the profile default API path is /ws_rimtrack_all/.
   */
  private resolveApiPath(): string {
    const normalizedApiPath = (this.apiPath || '/ws_rimtrack_all/').replace(/\/+$/, '') + '/';
    if (this.isDevServerHost(this.servername) && normalizedApiPath === '/ws_rimtrack_all/') {
      return '/ws_rimtrack_all_dev/';
    }
    return this.apiPath;
  }

  private isDevServerHost(servername: string): boolean {
    const host = (servername || '')
      .replace(/^(https?:\/\/)?/i, '')
      .split('/')[0]
      .split(':')[0]
      .toLowerCase()
      .trim();
    if (!host) {
      return false;
    }
    // dev.example.com, dev9.fleet.tn, dev
    const firstLabel = host.split('.')[0];
    return firstLabel === 'dev' || firstLabel.startsWith('dev');
  }

  updateUrl() {
    this.proto = protoForHost(this.servername);
    const apiPath = this.resolveApiPath();
    this.dns = absoluteUrl(apiPath, this.servername, { trailingSlash: true });
    this.policy = absoluteUrl(this.policyPath, this.servername);
    this.imagesDir = absoluteUrl('/images/', this.servername, { trailingSlash: true });
    this.imagesAssetDir = absoluteUrl('/images/assets/', this.servername, { trailingSlash: true });
    this.imagesCarsDir = absoluteUrl('/images/cars/', this.servername, { trailingSlash: true });
    this.imagesMODELDir = absoluteUrl('/images/models/', this.servername, { trailingSlash: true });
    this.imagesPOIDir = absoluteUrl('/images/poi/', this.servername, { trailingSlash: true });
    this.filesDir = absoluteUrl(this.filesPath === '/' ? '/' : this.filesPath, this.servername, {
      trailingSlash: false,
    });
    this.nominatim_dns = absoluteUrl(this.nominatimPath, this.servername);
    this.addok_dns = absoluteUrl(this.addokPath, this.servername, { trailingSlash: true });
    this.time_difference = new Date().getTimezoneOffset();
    console.log('[GlobalService] server=', this.servername, 'apiPath=', apiPath, 'dns=', this.dns);
  }

  setservername(servername: string) {
    this.servername = servername;
    this.proto = protoForHost(servername);
    this.updateUrl();
  }

  getnominatim_dns() {
    return this.nominatim_dns;
  }
  getimagesPOIDir() {
    return this.imagesPOIDir;
  }
  getimagesDir() {
    return this.imagesDir;
  }
  gettime_difference() {
    return this.time_difference;
  }
  getimagescar() {
    return this.imagesCarsDir;
  }
  getFileDir() {
    return this.filesDir;
  }
  getimagesMODELDir() {
    return this.imagesMODELDir;
  }
  getimagesCarsDir() {
    return this.imagesCarsDir;
  }
  getaddok_dns() {
    return this.addok_dns;
  }
  getimagesAssetDir() {
    return this.imagesAssetDir;
  }
}
